import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Button,
    Collapse,
    CircularProgress
} from '@mui/material';
import {
    LocalShipping as BookingIcon,
    Today as TodayIcon,
    CalendarMonth as MonthIcon,
    Pending as PendingIcon,
    LocalShipping as LocalShippingIcon,
    FlightTakeoff as FlightTakeoffIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import BookingList from './bookings/BookingList';
import { bookingService } from '../../services/bookingService';
import authService from '../../services/authService';
import { formatAmount } from '../../utils/formatters';

const BookingManagement = () => {
    const [stats, setStats] = useState(null);
    const [showStats, setShowStats] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [showAllWidgets, setShowAllWidgets] = useState(false);
    const [listFilters, setListFilters] = useState({});
    const { showSuccess, showError } = useApiHandler();

    // Check user permissions
    const canViewRevenue = authService.canViewRevenue();

    // Load persisted filters from localStorage
    const loadPersistedFilters = () => {
        try {
            const saved = localStorage.getItem('booking_filters_persistence');
            if (saved) {
                const filters = JSON.parse(saved);
                // Convert filter names to match API expectations
                const apiFilters = {};
                if (filters.searchText) apiFilters.search = filters.searchText;
                if (filters.fromDate) apiFilters.date_from = filters.fromDate;
                if (filters.toDate) apiFilters.date_to = filters.toDate;
                if (filters.selectedCustomer?.id) apiFilters.customer_id = filters.selectedCustomer.id;
                if (filters.statusFilter) apiFilters.status = filters.statusFilter;
                if (filters.branchFilter) apiFilters.branch_id = filters.branchFilter;
                return apiFilters;
            }
        } catch (error) {
            console.error('Error loading persisted booking filters:', error);
        }
        return {};
    };

    // Statistics are no longer fetched on page load. They are only loaded when the
    // user explicitly opens the panel via the "View Statistics" button, and always
    // reflect the currently applied booking filters.

    const loadBookingStats = async (filters = {}) => {
        try {
            setStatsLoading(true);
            const response = await bookingService.getBookingStats(filters);
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
            showError('Error loading booking statistics');
        } finally {
            setStatsLoading(false);
        }
    };

    // Filters currently applied to the booking list (falls back to persisted filters
    // before the list has reported its state).
    const getCurrentStatsFilters = () => (
        Object.keys(listFilters).length > 0 ? listFilters : loadPersistedFilters()
    );

    const handleToggleStats = () => {
        const next = !showStats;
        setShowStats(next);
        if (next) {
            loadBookingStats(getCurrentStatsFilters());
        }
    };

    const refreshStats = (filters = {}) => {
        // Always track the latest filters so the panel is correct whenever it is opened.
        setListFilters(filters);
        // Only re-fetch while the panel is open (avoids background stats calls on load
        // and on every list refresh when the user hasn't asked to see stats).
        if (showStats) {
            const filtersToUse = Object.keys(filters).length > 0 ? filters : loadPersistedFilters();
            loadBookingStats(filtersToUse);
        }
    };

    const handleViewMoreToggle = () => {
        setShowAllWidgets(!showAllWidgets);
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Booking Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Create, manage and track courier bookings and shipments
                </Typography>
            </Box>

            {/* Statistics header + toggle (statistics load only on demand) */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}>
                <Typography variant="h6" color="text.secondary">
                    Booking Statistics
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {showStats && stats && (
                        <Button
                            variant="outlined"
                            onClick={handleViewMoreToggle}
                            startIcon={showAllWidgets ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            size="small"
                        >
                            {showAllWidgets ? 'Show Less' : 'View More Stats'}
                        </Button>
                    )}
                    <Button
                        variant={showStats ? 'outlined' : 'contained'}
                        onClick={handleToggleStats}
                        disabled={statsLoading}
                        startIcon={showStats ? <ExpandLessIcon /> : <AssessmentIcon />}
                        size="small"
                    >
                        {showStats ? 'Hide Statistics' : 'View Statistics'}
                    </Button>
                </Box>
            </Box>

            {/* Statistics Cards */}
            {showStats && (
                statsLoading && !stats ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : stats && (
                <>
                    <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: 2 }}>
                        {/* Total Bookings */}
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                            <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                        <BookingIcon color="primary" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                        <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                            Total
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="primary" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                        {stats.total_bookings}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label="Filtered Results" 
                                            color="primary" 
                                            size="small" 
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Today's Bookings */}
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                            <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                        <TodayIcon color="success" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                        <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                            Today
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="success.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                        {stats.today_bookings}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={stats.filter_info?.has_filters ? 'Filtered' : 'Today'} 
                                            color={stats.filter_info?.today_in_range ? "success" : "default"} 
                                            size="small" 
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* This Month */}
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                            <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                        <MonthIcon color="info" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                        <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                            This Month
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="info.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                        {stats.this_month_bookings}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={stats.filter_info?.has_filters ? 'Filtered' : 'Current Month'} 
                                            color="info" 
                                            size="small" 
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Pending Bookings */}
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                            <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                        <PendingIcon color="warning" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                        <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                            Pending
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="warning.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                        {stats.pending_bookings}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label="Awaiting Pickup" 
                                            color="warning" 
                                            size="small" 
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Additional Statistics Cards - Collapsible */}
                    <Collapse in={showAllWidgets}>
                        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: 4 }}>

                    {/* Picked Up Bookings */}
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                    <LocalShippingIcon color="info" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                        Picked Up
                                    </Typography>
                                </Box>
                                <Typography variant="h4" color="info.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                    {stats.picked_up_bookings}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={stats.filter_info?.has_filters ? 'Filtered' : 'All Time'} 
                                        color="info" 
                                        size="small" 
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* In Transit Bookings */}
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                    <FlightTakeoffIcon color="primary" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                        In Transit
                                    </Typography>
                                </Box>
                                <Typography variant="h4" color="primary.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                    {stats.in_transit_bookings}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={stats.filter_info?.has_filters ? 'Filtered' : 'All Time'} 
                                        color="primary" 
                                        size="small" 
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Out for Delivery Bookings */}
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                    <LocalShippingIcon color="secondary" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                        Out for Delivery
                                    </Typography>
                                </Box>
                                <Typography variant="h4" color="secondary.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                    {stats.out_for_delivery_bookings}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={stats.filter_info?.has_filters ? 'Filtered' : 'All Time'} 
                                        color="secondary" 
                                        size="small" 
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Delivered Bookings */}
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Card sx={{ height: '100%', minHeight: { xs: 120, sm: 140, md: 160 } }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                    <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: { xs: 20, sm: 24, md: 28 } }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                        Delivered
                                    </Typography>
                                </Box>
                                <Typography variant="h4" color="success.main" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                                    {stats.delivered_bookings}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={stats.filter_info?.has_filters ? 'Filtered' : 'All Time'} 
                                        color="success" 
                                        size="small" 
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>


                        </Grid>
                    </Collapse>
                </>
                )
            )}

            {/* Booking Management */}
            <Paper sx={{ width: '100%' }}>
                <BookingList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
};

export default BookingManagement; 